import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

const RELOAD_KEY = 'prism-chunk-reload';
/** A second chunk failure on the same URL inside this window is a real outage, not a stale tab. */
const RELOAD_GUARD_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
    const err = (error as { rejection?: unknown })?.rejection ?? error;
    const name = (err as Error)?.name ?? '';
    const message = (err as Error)?.message ?? String(err ?? '');
    return name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/i.test(message);
}

/**
 * A deploy replaces every hashed bundle, so a tab still running the old build gets
 * index.html back for any lazy route it hasn't opened yet. Reload the page at the
 * URL it was navigating to, which picks up the new build and lands where the user
 * meant to go.
 */
@Injectable({ providedIn: 'root' })
export class ChunkReloadService {
    private reloading = false;

    constructor(private router: Router) {
        this.router.events.pipe(
            filter((event): event is NavigationError => event instanceof NavigationError),
            filter(event => isChunkLoadError(event.error)),
        ).subscribe(event => this.reloadTo(event.url));
    }

    /** Returns false when the guard blocked a reload, so the caller should surface the error. */
    public reloadTo(url: string): boolean {
        if (this.reloading) return true;

        const target = url.startsWith('/') ? url : `/${url}`;
        try {
            const last = JSON.parse(sessionStorage.getItem(RELOAD_KEY) || 'null') as { url: string; at: number } | null;
            if (last && last.url === target && Date.now() - last.at < RELOAD_GUARD_MS) {
                return false;
            }
            sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ url: target, at: Date.now() }));
        } catch {
            // storage blocked: still reload once; `reloading` stops repeats in this page
        }

        this.reloading = true;
        window.location.assign(target);
        return true;
    }
}

/** Chunk failures outside a navigation (and the router's rethrow) end up here. */
@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
    // Injector, not the service: ErrorHandler is built at bootstrap, before the Router should be
    constructor(private injector: Injector) {
        super();
    }

    override handleError(error: unknown): void {
        if (isChunkLoadError(error)) {
            const here = window.location.pathname + window.location.search + window.location.hash;
            if (this.injector.get(ChunkReloadService).reloadTo(here)) return;
        }
        super.handleError(error);
    }
}
